import React, { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'antd';
import styles from './adminUsers.module.scss';
import Header from '../../../components/header/Header';
import Button from '../../../components/button/Button';
import Search from '../../../components/search/Search';
import Select from '../../../components/select/Select';
import { gql, useMutation } from '@apollo/client';
import { User, UserRoleEnum, SelectOption } from '../../../types';
import IconRotating from '../../../assets/icons/rotating_arrow.svg?react';
import InviteModal from './invite-modal/InviteModal';
import ChevronIcon from 'src/assets/icons/chevron_down.svg?react';
import Spinner from '../../../components/spinner/Spinner';
import Footer from '../../../components/footer/Footer';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { useHistory } from 'react-router-dom';
import { useToast } from 'src/hooks/useToast';
import { BulkInviteSlidingPanel } from "./bulk-invite-slide/bulkInviteSlidePanel";
import { useAccountUsersLazyQuery, useGetAccountSchoolsLazyQuery, SchoolUser, useSchoolUsersLazyQuery } from '../../../api/graphql-codegen';
import { useUser } from '../../../hooks/useUser';
import { districtAdminRoles, countries, IconUserManage, IconDistAdmin, IconUser } from '../../../constants';
import { filterUsers } from '../../../utils/helpers';
import { InviteSlidingPanel } from './invite-slide/inviteSlidePanel';
import { useClientPagination } from '@src/hooks';
// import Pagination from '@src/components/pagination/Pagination';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import type { MenuProps, PaginationProps } from 'antd';
import { Pagination } from "antd";
import './antdStyles.css';
import UserColumn from './userColumn/UserColumn';
import NormalModal from '@src/components/modal/normal-modal/NormalModal';
import IconWarning from '@src/assets/icons/modal-warning.svg?react';
import NotificationIcon from '@src/assets/icons/notification.svg?react';
import Tooltip from '@src/components/tooltip/Tooltip';
import { logger } from '../../../utils/logger';

export const querySchoolUsers = gql`
  query SchoolUsers {
    schoolUsers {
      id
      email
      firstName
      lastName
      status
      customer {
        id
        name
        trial
      }
      role
      admin
      status
      districtSchools
      lastLogin
      completedLessons {
        unitId
        lessonId
        unitName
        lessonName
      }
      sllCompletedLessons {
        lessonId
        lessonName
      }
    }
  }
`;

const mutationCopyPasswordLink = gql`
  mutation CopyPasswordLink($input: EmailInput!) {
    copyPasswordLink(input: $input)
  }
`;

const mutationEmailPasswordLink = gql`
  mutation EmailPasswordLink($input: EmailInput!) {
    emailPasswordLink(input: $input)
  }
`;

export type QueryUsers = {
  schoolUsers: SchoolUser[];
}

function AdminUsers() {
  const [reloadData, {data: adminUsers, loading: adminUsersLoading}] = useSchoolUsersLazyQuery({fetchPolicy: 'network-only'});
  const [fetchDistrictAccountUsers, { data: districtUsers, loading: districtUsersLoading }] = useAccountUsersLazyQuery({fetchPolicy: 'network-only'});
  const [me] = useUser();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [bulkInviteSlide, setBulkInviteSlide] = useState(false);
  const [inviteSlide, setInviteSlide] = useState(false);
  const [search, setSearch] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [schoolOptions, setSchoolOptions] = useState<SelectOption[]>([]);
  const [perPage, setPerPage] = useState(50);
  const [current, setCurrent] = useState(1);
  const [showRoleMenu, setShowRoleMenu] = useState(false);

  const [getSchools] = useGetAccountSchoolsLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted(res) {
      const allowedSchoolIds = me?.districtSchools ? JSON.parse(me?.districtSchools) : [];
      const allowedSchoolsOptions = (res?.accountSchools?.schools ?? []).filter(s => !!s?.id && allowedSchoolIds.includes(s.id)).map((s) => {
        return {
          value: s!.id,
          label: s!.name,
        };
      });
      setSchoolOptions(allowedSchoolsOptions);
    }
  });

  const queryParams = useQueryParams();

  const showInviteParam = queryParams.get('invite');
  const showBulkInvitesParam = queryParams.get('bulk-invites');

  const history = useHistory();

  const {successToast, errorToast} = useToast();

  const [users, loading] = useMemo(() => {
    let users = [...(adminUsers?.schoolUsers || districtUsers?.accountUsers || [])];
    users.sort((a, b) => (b?.lastName || '') >= (a?.lastName || '') ? -1 : 1);

    users = filterUsers(users as User[], search);
    if (roleFilter) {
      users = users.filter(d => d?.role === roleFilter);
    }

    if (schoolFilter) {
      users = users.filter(d => (d?.schools || []).map(r => r?.id).includes(schoolFilter));
    }

    if (countryFilter) {
      users = users.filter(d => countries[(d?.id || '').substring(0, 2)] === countryFilter);
    }
    return [users, adminUsersLoading || districtUsersLoading];
  }, [adminUsers?.schoolUsers, adminUsersLoading, countryFilter, districtUsers?.accountUsers, districtUsersLoading, roleFilter, schoolFilter, search]);

  useEffect(() => {
    if (bulkInviteSlide) document.body.style.overflow = "hidden";
    else {
      document.body.style.overflow = "auto";
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    }
  }, [bulkInviteSlide]);

  useEffect(() => {
    if (me?.role) {
      if (me.role === UserRoleEnum.DistrictAdmin) {
        fetchDistrictAccountUsers();
      } else {
        reloadData();
      }
    }
  }, [fetchDistrictAccountUsers, me?.role, reloadData]);

  const isDistrictAdmin = useMemo(() => me?.role === UserRoleEnum.DistrictAdmin, [me?.role]);

  const handleChangeFilter = useCallback((setter: Dispatch<string>) => (o: SelectOption) => {
    setter(o.value);
  }, []);
  const handleChangeSearch = useCallback((v) => {
    setSearch(v);
  }, []);

  const {dataSlice: filteredData, onChangePage} = useClientPagination({
    data: users || [],
    perPage,
  });

  useEffect(() => {
    if (me?.customer?.id && me?.role === UserRoleEnum.DistrictAdmin) {
      getSchools({variables: { input: { id: me.customer.id } } });
    }
  }, [getSchools, me?.customer?.id, me?.role]);

  useEffect(() => {
    if (showInviteParam) {
      history.replace(history.location.pathname);
      // handleOpenInviteModal();   // Open Invite slide instead of modal OP-1038
      handleOpenInviteSlide();
    }
  }, [history, showInviteParam]);

  useEffect(() => {
    if (showBulkInvitesParam) {
      history.replace(history.location.pathname);
      handleBulkInviteSlide();
    }
  }, [history, showBulkInvitesParam]);

  const handleBulkInviteSlide = () => setBulkInviteSlide(true);
  const handleOpenInviteSlide = () => setInviteSlide(true);

  const handleModalCloseAction = () => {
    setUser(undefined);
    setShowInviteModal(false);
    setShowResetPasswordModal(false);
  };

  const handleShowEditPopup = useCallback((user: User, action: 'EDIT' | 'PASSWORD_RESET') => {
    setUser(user);
    if (action === 'EDIT') {
      setShowInviteModal(true);
    }
    if (action === 'PASSWORD_RESET') {
      setShowResetPasswordModal(true);
    }
  }, []);

  const [copyPasswordLink, { loading: copyActionLoading }] = useMutation<{ copyPasswordLink: string }>(mutationCopyPasswordLink, {
    onCompleted: (data) => {
      navigator.clipboard.writeText(data.copyPasswordLink).then(() => {
        handleModalCloseAction()
        successToast(`${user?.firstName} ${user?.lastName}'s Password Reset Link Copied!`);
      })
        .catch((e) => {
          logger.info('Copy to clipboard is not working when DevTool is focused', e);
        });
    },
    onError: (error) => {
      logger.debug(`[Error]`, error);
      const errorMessage = error?.message || error?.graphQLErrors?.[0]?.message;
      errorToast(errorMessage);
    },
  });

  const [emailPasswordLink, { loading: emailActionLoading }] = useMutation<{ emailPasswordLink: boolean }>(mutationEmailPasswordLink, {
    onCompleted: (data) => {
      if (data.emailPasswordLink) {
        handleModalCloseAction()
        successToast(`${user?.firstName} ${user?.lastName} has been sent a Password Reset Email`);
      }
    },
    onError: (error) => {
      logger.debug(`[Error]`, error);
      const errorMessage = error?.message || error?.graphQLErrors?.[0]?.message;
      errorToast(errorMessage);
    },
  });

  const handleModalAction = useCallback((userEmail: string, option: 'COPY_ACTION' | 'EMAIL_ACTION' ) => async () => {
    try {
      if (option === 'COPY_ACTION') {
        await copyPasswordLink({ variables: { input: { email: userEmail } } });
      }
      if (option === 'EMAIL_ACTION') {
        await emailPasswordLink({ variables: { input: { email: userEmail } } });
      }
    } catch (e) {
      errorToast((e as any)?.message || '');
    }
  }, [copyPasswordLink, errorToast, emailPasswordLink]);

  const handleCopyToClipboard = useCallback((text: string) => {
    successToast(text);
  }, [successToast]);

  const handleChangePage = (page: number) => {
    setCurrent(page);
    onChangePage(page);
  };

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = (current, pageSize) => {
    setCurrent(current);
    setPerPage(pageSize);
  };

  const handleReload = useCallback(() => {
    if (me?.role === UserRoleEnum.DistrictAdmin) {
      fetchDistrictAccountUsers();
    } else {
      reloadData();
    }
  },[me?.role, fetchDistrictAccountUsers, reloadData])

  const onSelectRole = (e: any, index: number) => {
    if (districtAdminRoles[index].value) {
      setRoleFilter(districtAdminRoles[index].value)
    }
    setShowRoleMenu(false)
  }

  const roleSelectMenu: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <div className={styles.dropDownMenuitem}>
          <Button label='OP District Administrator' size='extraSmall' className={styles.dropDownItem} leftIcon={<LordIcon src={IconDistAdmin} isHover={true} colors={{primary: '#121331', secondary: '#FFB073'}} size={30}/>} />
        </div>
      ),
      onClick: (e) => onSelectRole(e, 0)
    },
    {
      key: '2',
      label: (
        <div className={styles.dropDownMenuitem}>
          <Button label='OP School Administrator' size='extraSmall' className={styles.dropDownItem} leftIcon={<LordIcon src={IconUserManage} isHover={true} colors={{primary: '#121331', secondary: '#FFB073'}} size={30}/>} />
        </div>
      ),
      onClick: (e) => onSelectRole(e, 1)
    },
    {
      key: '3',
      label: (
        <div className={styles.dropDownMenuitem}>
          <Button label='Educator' size='extraSmall' className={styles.dropDownItem} leftIcon={<LordIcon src={IconUser} isHover={false} colors={{primary: '#121331', secondary: '#FFB073'}} size={30}/>} />
        </div>
      ),
      onClick: (e) => onSelectRole(e, 2)
    }
  ]

  const handleResetFilters = useCallback(() => {
    setSchoolFilter('');
    setRoleFilter('');
    setCountryFilter('');
    setSearch('');
  }, []);

  const handleChangeRole = useCallback((visible: boolean) => {
    setShowRoleMenu(visible)
  },[])

  const roleSelected = useMemo(() => {
    if (roleFilter) {
      const role = districtAdminRoles.find(item => item.value === roleFilter)
      return role ? role.label : null;
    }
    return null;
  }, [roleFilter])

  return (
    <>
      <Header inviteUser={handleOpenInviteSlide} bulkInvite={handleBulkInviteSlide} reLoadData={handleReload}/>
      <div className={styles.userMain}>
      <div className={styles.shadowView}></div>
      <div className={styles.page}>
        <div className={styles.header}>
          <LordIcon src={IconUserManage} size={70} isHover={true} colors={{ primary: '#121330', secondary: '#FFB073'}}/>
          <h2 className={styles.title}>{'User Management'}</h2>
        </div>
        {isDistrictAdmin ? (
          <div className={styles.search}>
            <Search onChange={handleChangeSearch} value={search} placeholder='Search Users'/>
            <div className={styles.filters}>
              <Select inDropDown={false} value={schoolFilter} onChange={handleChangeFilter(setSchoolFilter)}
                      label={'Filter by School'} placeholder='All' options={schoolOptions}/>
              <div>
                <div className={styles.roleTitle}>Filter by Role</div>
                <Dropdown menu={{ items: roleSelectMenu }} trigger={['click']} placement="bottom" onOpenChange={handleChangeRole} open={showRoleMenu}>
                  <div className={styles.roleSelect}>
                    <div className={styles.selectedRole} style={{color: roleFilter ? '#303133' : '#999999', fontSize: roleFilter ? 14 : 13}}>{roleFilter ? roleSelected : 'All'}</div>
                    <div className={styles.chevronIcon} style={{transform: showRoleMenu ? 'rotateZ(180deg)' : 'rotateZ(0deg)'}}>
                      <ChevronIcon className={styles.chevron}/>
                    </div>
                  </div>
                </Dropdown>
              </div>
              <div className={styles.actions}>
                <Button onClick={handleResetFilters} className={styles.resetButton} label={'RESET'}
                        size={'extraSmall'} outline={true} color={'green'}
                        leftIcon={<IconRotating className={styles.resetIcon}/>}/>
                <Button onClick={handleOpenInviteSlide} className={styles.inviteButton} label={'INVITE'}
                        size={'extraSmall'} color={'orange'}/>
              </div>
            </div>
          </div>) : (
            <div className={styles.search}>
              <Search onChange={handleChangeSearch} value={search} placeholder='Search Users'/>
              <div className={styles.filters}>
                <div>
                  <div className={styles.roleTitle}>Filter by Role</div>
                  <Dropdown menu={{ items: roleSelectMenu }} trigger={['click']} placement="bottom" onOpenChange={handleChangeRole} open={showRoleMenu}>
                    <div className={styles.roleSelect}>
                      <div className={styles.selectedRole} style={{color: roleFilter ? '#303133' : '#999999', fontSize: roleFilter ? 14 : 13}}>{roleFilter ? roleSelected : 'All'}</div>
                      <div className={styles.chevronIcon} style={{transform: showRoleMenu ? 'rotateZ(180deg)' : 'rotateZ(0deg)'}}>
                        <ChevronIcon className={styles.chevron}/>
                      </div>
                    </div>
                  </Dropdown>
                </div>
                <div className={styles.actions}>
                  <Button onClick={handleResetFilters} className={styles.resetButton} label={'RESET'}
                          size={'extraSmall'} outline={true} color={'green'}
                          leftIcon={<IconRotating className={styles.resetIcon}/>}/>
                </div>
                <div className={styles.inviteView}>
                  <Button onClick={handleOpenInviteSlide} className={styles.inviteButton} label={'INVITE'}
                          size={'extraSmall'} color={'orange'}/>
                </div>
              </div>
            </div>
          )
        }
        <div className={styles.content}>
          {loading || !me?.role ? <div><Spinner/></div> : 
          <div className={styles.tableWrapper}>
            <div className={isDistrictAdmin ? styles.widerHeaderFilter : styles.headerFilter}>
              <div className={styles.headerTitle}>Name</div>
              <div className={styles.headerTitle}>Email</div>
              {isDistrictAdmin ? (
                <div className={styles.headerTitle}>School</div>                  
              ) : null}
              <div className={styles.headerCenterTitle}>Last Activity</div>
              <div className={styles.headerCenterTitle}>Total Logins</div>
              <div className={styles.headerCenterTitle}>Role</div>
              <div className={styles.headerCenterTitle}>Reset Password</div>
              <div className={styles.headerCenterTitle}>Edit</div>
            </div>
            {filteredData.map(user => {
              return (
                <UserColumn key={user.id} user={user} isDistrictAdmin={isDistrictAdmin}
                  handleCopyToClipboard={handleCopyToClipboard} 
                  handleUserEdit={handleShowEditPopup}/>
              )
            })}
            {users && <div className={styles.paginationWrapper}>
              <Pagination
                showSizeChanger
                onShowSizeChange={onShowSizeChange}
                pageSize={perPage}
                current={current}
                total={users.length}
                onChange={handleChangePage}
                className={styles.pagination}
              />
            </div>}
          </div>}
        </div>
      </div>
      </div>
      <Footer/>
      <BulkInviteSlidingPanel bulkInviteSlide={bulkInviteSlide} setBulkInviteSlide={setBulkInviteSlide}
                              reloadData={reloadData}/>
      <InviteSlidingPanel bulkInviteSlide={inviteSlide} setBulkInviteSlide={setInviteSlide}
                              reLoadData={reloadData}/>
      {schoolOptions ? (
        <InviteModal user={user} reLoadData={reloadData} open={showInviteModal} onClose={handleModalCloseAction} schoolOptions={schoolOptions} handleReload={handleReload}/>
      ) : null}
      {(showResetPasswordModal && !!user) && (
        <NormalModal
          logoIcon={<IconWarning />}
          onClose={handleModalCloseAction}
          title={'Password Reset'}
          open={showResetPasswordModal && !!user}
          description={`Select from the following two options to reset ${user.firstName}'s Password.`}
          leftButton={
            <Button
              isLoading={copyActionLoading}
              size='small'
              className={styles.cancelButton}
              onClick={handleModalAction(user.email, 'COPY_ACTION')}
              label="COPY RESET LINK"
              leftIcon={
                <Tooltip 
                  color="light"
                  position={'left'}
                  tooltip={`Click to copy a link that you can send to ${user.firstName} to reset their password.`}
                >
                  <NotificationIcon />
                </Tooltip>
              }
            />
          }
          rightButton={
            <Button
              isLoading={emailActionLoading}
              size='small'
              className={styles.actionButton}
              onClick={handleModalAction(user.email, 'EMAIL_ACTION')}
              label="EMAIL RESET LINK" leftIcon={
                <Tooltip 
                  color="light"
                  position={'left'}
                  tooltip={`${user.firstName} will receive an email with a link to reset their password.`}
                >
                  <NotificationIcon />
                </Tooltip>
              }
            />
          }
        />
      )}
    </>
  );
}

export default AdminUsers;
